import type { NoteView } from "@screener/protocol";

/**
 * Локальное состояние редактора заметок §6.4 детальной панели §9. Заметки
 * пишутся запросным каналом (`use-notes.ts`), а этот файл держит только то,
 * что происходит между отправкой и ответом: черновик новой заметки, черновик
 * правки существующей и последнюю неудачу записи.
 *
 * Неудача записи не должна стирать введённый текст (web-density-detail
 * «Неудача заметки не рушит панель») — поэтому черновик и ошибка живут
 * раздельно, и `*-failed` не трогает текст.
 */
export interface NotesEditorState {
  /** Текст новой заметки, ещё не отправленной. */
  draft: string;
  /** Заметка, которую сейчас правят, и её черновой текст. */
  editing: { id: string; text: string } | null;
  /** Последнее действие не удалось — раздел говорит об этом словом. */
  error: "create" | "edit" | null;
  /**
   * Последнее удаление не удалось — раздел различает «такой заметки уже нет»
   * и «не удалось сейчас, повторите»: отказ записи не имеет права молчать
   * (review stage5-notes-and-pin, п. 2).
   */
  deleteError: { id: string; reason: "not-found" | "unavailable" } | null;
}

export const initialNotesEditorState: NotesEditorState = {
  draft: "",
  editing: null,
  error: null,
  deleteError: null,
};

export type NotesEditorAction =
  | { type: "draft-changed"; text: string }
  | { type: "create-failed" }
  | { type: "create-succeeded" }
  | { type: "edit-started"; note: NoteView }
  | { type: "edit-changed"; text: string }
  | { type: "edit-cancelled" }
  | { type: "edit-failed" }
  | { type: "edit-succeeded" }
  | { type: "delete-failed"; id: string; reason: "not-found" | "unavailable" }
  | { type: "delete-succeeded" };

export function reduceNotesEditor(
  state: NotesEditorState,
  action: NotesEditorAction,
): NotesEditorState {
  switch (action.type) {
    case "draft-changed":
      return { ...state, draft: action.text, error: null };
    case "create-failed":
      // Черновик остаётся: перечитывать его человеку не с руки.
      return { ...state, error: "create" };
    case "create-succeeded":
      return { ...state, draft: "", error: null };
    case "edit-started":
      return { ...state, editing: { id: action.note.id, text: action.note.text }, error: null };
    case "edit-changed":
      return state.editing === null
        ? state
        : { ...state, editing: { ...state.editing, text: action.text } };
    case "edit-cancelled":
      return { ...state, editing: null, error: null };
    case "edit-failed":
      // Правка остаётся в поле — так же, как черновик при неудаче создания.
      return { ...state, error: "edit" };
    case "edit-succeeded":
      return { ...state, editing: null, error: null };
    case "delete-failed":
      return { ...state, deleteError: { id: action.id, reason: action.reason } };
    case "delete-succeeded":
      return { ...state, deleteError: null };
    default:
      return action satisfies never;
  }
}
